import { useState, useEffect } from 'react';
import { DetailModal, type DetailModalSection, type DetailModalStatCard } from './DetailModal';
import { RejectOrderModal, type RejectionReason } from './RejectOrderModal';
import { AlertModal } from './AlertModal';
import { AssignCourierModal } from './AssignCourierModal';
import { Button, Badge } from '@atoms/index';
import { restaurantOrderService } from '@services/restaurantOrderService';
import type { Order, OrderStatus } from '@/types';
import { formatCurrency } from '@/utils/currency';
import { formatPaymentMethod } from '@/utils/formatters';
import { cn } from '@/utils/cn';

interface OrderDetailModalProps {
  orderId: string | null;
  onClose: () => void;
  onStatusChange?: (orderId: string, newStatus: OrderStatus) => void;
  onReject?: (orderId: string, reason: RejectionReason, message?: string) => Promise<void>;
  onCourierAssigned?: () => void;
}

const statusLabels: Record<OrderStatus, string> = {
  CREATED: 'Yeni Sipariş',
  SCHEDULED: 'Planlanmış',
  PENDING: 'Beklemede',
  PREPARING: 'Hazırlanıyor',
  READY: 'Hazır',
  ON_WAY: 'Yolda',
  DELIVERED: 'Teslim Edildi',
  CANCELLED: 'İptal Edildi',
};

const statusColors: Record<OrderStatus, string> = {
  CREATED: 'bg-blue-100 text-blue-800',
  SCHEDULED: 'bg-purple-100 text-purple-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  PREPARING: 'bg-orange-100 text-orange-800',
  READY: 'bg-green-100 text-green-800',
  ON_WAY: 'bg-indigo-100 text-indigo-800',
  DELIVERED: 'bg-gray-100 text-gray-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

export function OrderDetailModal({
  orderId,
  onClose,
  onStatusChange,
  onReject,
  onCourierAssigned,
}: OrderDetailModalProps) {
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showAssignCourierModal, setShowAssignCourierModal] = useState(false);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

  const reloadOrder = () => {
    if (!orderId) return;
    restaurantOrderService
      .getOrder(orderId)
      .then((orderData) => setOrder(orderData))
      .catch((err) => {
        setError(err.response?.data?.error?.message || 'Sipariş yüklenirken bir hata oluştu');
      });
  };

  useEffect(() => {
    if (!orderId) return;

    setIsLoading(true);
    setError(null);

    restaurantOrderService
      .getOrder(orderId)
      .then((orderData) => {
        setOrder(orderData);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error?.message || 'Sipariş yüklenirken bir hata oluştu');
        setIsLoading(false);
      });
  }, [orderId]);

  if (!orderId) return null;

  if (isLoading) {
    return (
      <DetailModal
        title="Sipariş Detayları"
        onClose={onClose}
        sections={[
          {
            title: 'Yükleniyor...',
            icon: '⏳',
            content: (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-gray-600">Sipariş bilgileri yükleniyor...</p>
              </div>
            ),
          },
        ]}
      />
    );
  }

  if (error || !order) {
    return (
      <DetailModal
        title="Sipariş Detayları"
        onClose={onClose}
        sections={[
          {
            title: 'Hata',
            icon: '❌',
            content: (
              <div className="text-center py-8">
                <p className="text-red-600">{error || 'Sipariş bulunamadı'}</p>
                <Button variant="primary" onClick={onClose} className="mt-4">
                  Kapat
                </Button>
              </div>
            ),
          },
        ]}
      />
    );
  }

  const statCards: DetailModalStatCard[] = [
    {
      label: 'Toplam Tutar',
      value: formatCurrency(
        typeof order.total === 'number' && !isNaN(order.total)
          ? order.total
          : (order.subtotal || 0) + (order.deliveryFee || 0) + (order.serviceFee || 0) || 0,
      ),
      icon: '💰',
      iconBgColor: 'bg-green-50',
      valueColor: 'text-green-600',
    },
    {
      label: 'Durum',
      value: (
        <Badge
          variant={
            order.status === 'CREATED'
              ? 'info'
              : order.status === 'PREPARING'
              ? 'warning'
              : order.status === 'READY'
              ? 'success'
              : 'danger'
          }
        >
          {statusLabels[order.status]}
        </Badge>
      ),
      icon: '📦',
      iconBgColor: 'bg-blue-50',
    },
    {
      label: 'Ödeme',
      value: (
        <Badge variant={order.paymentStatus === 'PAID' ? 'success' : 'warning'}>
          {order.paymentStatus === 'PAID' ? 'Ödendi' : 'Bekliyor'}
        </Badge>
      ),
      icon: '💳',
      iconBgColor: 'bg-purple-50',
    },
  ];

  const sections: DetailModalSection[] = [
    {
      title: 'Sipariş Bilgileri',
      icon: '📋',
      iconBgColor: 'bg-blue-50',
      gridCols: 2,
      content: (
        <>
          <div>
            <p className="text-sm text-gray-500 mb-1">Sipariş Numarası</p>
            <p className="font-medium text-gray-900">
              {order.orderNumber || order.id.slice(0, 8)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Sipariş Tarihi</p>
            <p className="font-medium text-gray-900">
              {new Date(order.createdAt).toLocaleString('tr-TR')}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Ödeme Yöntemi</p>
            <p className="font-medium text-gray-900">
              {order.paymentMethod ? formatPaymentMethod(order.paymentMethod) : '-'}
            </p>
          </div>
          {order.estimatedDeliveryTime && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Tahmini Teslimat</p>
              <p className="font-medium text-gray-900">
                {new Date(order.estimatedDeliveryTime).toLocaleString('tr-TR')}
              </p>
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Teslimat Adresi',
      icon: '📍',
      iconBgColor: 'bg-green-50',
      gridCols: 1,
      content: (
        <div className="space-y-2">
          <p className="font-medium text-gray-900">{order.deliveryAddress.label}</p>
          <p className="text-sm text-gray-600">{order.deliveryAddress.fullAddress}</p>
          {order.deliveryAddress.building && (
            <p className="text-sm text-gray-600">
              Bina: {order.deliveryAddress.building}
              {order.deliveryAddress.floor && `, Kat: ${order.deliveryAddress.floor}`}
              {order.deliveryAddress.apartment && `, Daire: ${order.deliveryAddress.apartment}`}
            </p>
          )}
          {order.deliveryAddress.instructions && (
            <div className="mt-2 pt-2 border-t">
              <p className="text-sm text-gray-500 mb-1">Özel Talimatlar</p>
              <p className="text-sm text-gray-700">{order.deliveryAddress.instructions}</p>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Sipariş Ürünleri',
      icon: '🍽️',
      iconBgColor: 'bg-orange-50',
      gridCols: 1,
      content: (
        <div className="space-y-3">
          {order.items.map((item, idx) => (
            <div key={idx} className="flex justify-between items-start p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-gray-900">
                  {item.quantity}x {item.menuItemName}
                </p>
                {item.optionNames && item.optionNames.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    {item.optionNames.join(', ')}
                  </p>
                )}
                {item.removedIngredientNames && item.removedIngredientNames.length > 0 && (
                  <p className="text-sm text-red-600 mt-1 font-medium">
                    Çıkarılan: {item.removedIngredientNames.join(', ')}
                  </p>
                )}
                {item.specialInstructions && (
                  <p className="text-sm text-gray-500 mt-1 italic">
                    Not: {item.specialInstructions}
                  </p>
                )}
              </div>
              <div className="text-right ml-4">
                <p className="font-medium text-gray-900">
                  {formatCurrency(item.unitPrice * item.quantity)}
                </p>
                <p className="text-xs text-gray-500">
                  {formatCurrency(item.unitPrice)} / adet
                </p>
              </div>
            </div>
          ))}
        </div>
      ),
    },
    ...((order.customerNotes || (order as any).customer_notes) ? [{
      title: 'Sipariş Notu' as const,
      icon: '📝' as const,
      iconBgColor: 'bg-red-50' as const,
      gridCols: 1 as const,
      content: (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-gray-800 font-medium">
            {order.customerNotes || (order as any).customer_notes}
          </p>
        </div>
      ),
    }] : []),
    {
      title: 'Fiyat Detayı',
      icon: '💵',
      iconBgColor: 'bg-yellow-50',
      gridCols: 1,
      content: (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Ara Toplam</span>
            <span className="font-medium text-gray-900">{formatCurrency(order.subtotal || 0)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Teslimat Ücreti</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(order.deliveryFee || 0)}
            </span>
          </div>
          {order.serviceFee && order.serviceFee > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Servis Ücreti</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(order.serviceFee)}
              </span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t font-bold text-lg">
            <span className="text-gray-900">Toplam</span>
            <span className="text-primary">
              {formatCurrency(
                typeof order.total === 'number' && !isNaN(order.total)
                  ? order.total
                  : (order.subtotal || 0) + (order.deliveryFee || 0) + (order.serviceFee || 0) || 0,
              )}
            </span>
          </div>
        </div>
      ),
    },
    ...(order.courier
      ? [{
          title: 'Kurye Bilgileri' as const,
          icon: '🛵' as const,
          iconBgColor: 'bg-green-50' as const,
          gridCols: 1 as const,
          content: (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Ad</p>
                <p className="font-medium text-gray-900">{order.courier.name || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Telefon</p>
                <p className="font-medium text-gray-900">{order.courier.phone || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Durum</p>
                <Badge variant={order.courier.status === 'ONLINE' ? 'success' : 'warning'}>
                  {order.courier.status}
                </Badge>
              </div>
            </div>
          ),
        }]
      : []),
  ];

  const canTransitionToPreparing = order.status === 'CREATED' && onStatusChange;
  const canTransitionToReady = order.status === 'PREPARING' && onStatusChange;
  const canReject = (order.status === 'CREATED' || order.status === 'PENDING') && onReject;
  const canAssignCourier =
    !order.courierId && (order.status === 'PREPARING' || order.status === 'READY');

  const handleReject = async (reason: RejectionReason, message?: string) => {
    if (!onReject) return;
    await onReject(order.id, reason, message);
    setShowRejectModal(false);
    onClose();
  };

  const handlePrintReceipt = async () => {
    try {
      await restaurantOrderService.printReceipt(order.id, 'receipt');
    } catch (error) {
      console.error('Failed to print receipt:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Fiş yazdırılırken bir hata oluştu' });
    }
  };

  const handlePrintKitchenTicket = async () => {
    try {
      await restaurantOrderService.printReceipt(order.id, 'kitchen');
    } catch (error) {
      console.error('Failed to print kitchen ticket:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Mutfak fişi yazdırılırken bir hata oluştu' });
    }
  };

  const actions = (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3 justify-end">
        {canTransitionToPreparing && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              onStatusChange?.(order.id, 'PREPARING');
              onClose();
            }}
          >
            👨‍🍳 Hazırlamaya Başla
          </Button>
        )}
        {canTransitionToReady && (
          <Button
            variant="success"
            size="sm"
            onClick={() => {
              onStatusChange?.(order.id, 'READY');
              onClose();
            }}
          >
            ✅ Hazır Olarak İşaretle
          </Button>
        )}
        {canAssignCourier && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowAssignCourierModal(true)}
          >
            🛵 Kurye Ata
          </Button>
        )}
        {canReject && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => setShowRejectModal(true)}
          >
            ❌ Siparişi Reddet
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onClose}>
          Kapat
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-3 border-t justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={handlePrintReceipt}
        >
          🖨️ Fiş Yazdır
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handlePrintKitchenTicket}
        >
          🍽️ Mutfak Fişi
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <DetailModal
        title={`Sipariş #${order.orderNumber || order.id.slice(0, 8)}`}
        subtitle={statusLabels[order.status]}
        onClose={onClose}
        statCards={statCards}
        sections={sections}
        actions={actions}
        maxWidth="2xl"
      />
      {showRejectModal && (
        <RejectOrderModal
          isOpen={showRejectModal}
          orderNumber={order.orderNumber || order.id.slice(0, 8)}
          onConfirm={handleReject}
          onClose={() => setShowRejectModal(false)}
        />
      )}
      {showAssignCourierModal && (
        <AssignCourierModal
          orderId={order.id}
          orderNumber={order.orderNumber || order.id.slice(0, 8)}
          isOpen={showAssignCourierModal}
          onClose={() => setShowAssignCourierModal(false)}
          onAssigned={() => {
            reloadOrder();
            onCourierAssigned?.();
            setAlertState({
              isOpen: true,
              title: 'Başarılı',
              message: 'Kurye başarıyla atandı.',
            });
          }}
        />
      )}
      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.title === 'Başarılı' ? 'success' : 'danger'}
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />
    </>
  );
}
